/**
 * QuestionPreview Component
 *
 * Interactive preview wrapper that renders survey-response question components
 * in the survey builder interface. Shows how questions will appear to respondents.
 */

import React, { useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { QuestionDTO } from '../dtos';
import { mapQuestionDTOToSurveyData } from '../utils/questionDataMapper';
import { QuestionRenderer } from '../../survey-response/components/QuestionRenderer';

// Styled components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const PreviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PreviewLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const PreviewContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  minHeight: '200px',
}));

const LanguageToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'uppercase',
    fontWeight: 500,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

// Component props
interface QuestionPreviewProps {
  question: QuestionDTO;
}

/**
 * QuestionPreview component displays an interactive preview of how
 * a question will appear to survey respondents
 */
export const QuestionPreview: React.FC<QuestionPreviewProps> = ({ question }) => {
  // Local state for preview (not persisted)
  const [previewAnswer, setPreviewAnswer] = useState<
    string | string[] | number | boolean | undefined
  >(undefined);
  const [previewComment, setPreviewComment] = useState<string>('');
  const [language, setLanguage] = useState<'en' | 'no'>('no');

  // Transform QuestionDTO to SurveyQuestionData
  const surveyQuestionData = mapQuestionDTOToSurveyData(question);

  // Handle language toggle
  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLanguage: 'en' | 'no' | null
  ) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  // Handle answer change
  const handleAnswerChange = (value: string | string[] | number | boolean | undefined) => {
    setPreviewAnswer(value);
  };

  // Handle comment change
  const handleCommentChange = (comment: string) => {
    setPreviewComment(comment);
  };

  return (
    <PreviewContainer elevation={0}>
      <PreviewHeader>
        <PreviewLabel>Preview</PreviewLabel>
        <LanguageToggle
          value={language}
          exclusive
          onChange={handleLanguageChange}
          aria-label="language toggle"
          size="small"
        >
          <ToggleButton value="no" aria-label="Norwegian">
            NO
          </ToggleButton>
          <ToggleButton value="en" aria-label="English">
            EN
          </ToggleButton>
        </LanguageToggle>
      </PreviewHeader>

      <PreviewContent>
        <QuestionRenderer
          question={surveyQuestionData}
          value={previewAnswer}
          comment={previewComment}
          language={language}
          onChange={handleAnswerChange}
          onCommentChange={handleCommentChange}
          disabled={false}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- Preview mode: autoFocus explicitly set to false
          autoFocus={false}
        />
      </PreviewContent>
    </PreviewContainer>
  );
};
